import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, notification } from "antd";
import { observer } from "mobx-react-lite";

import { deleteProfile } from "../../api/profile";
import { userStore } from "../../stores/userStore";
import { usePendingAction } from "../../utils/usePendingAction";

interface DeleteProfileModalProps {
  open: boolean;
  onClose: () => void;
  initialSuccess?: boolean;
}

export const DeleteProfileModal = observer(
  ({ open, onClose, initialSuccess = false }: DeleteProfileModalProps) => {
    const { t } = useTranslation();
    const [success, setSuccess] = useState(initialSuccess);
    const { pending: loading, run: runDelete } = usePendingAction();

    const handleClose = () => {
      if (loading) return;
      setSuccess(false);
      onClose();
    };

    const handleDelete = async () => {
      await runDelete(async () => {
        try {
          await deleteProfile();
          setSuccess(true);
        } catch {
          notification.error({ message: t("errors.shared.common_error") });
        }
      });
    };

    return (
      <Modal
        open={open}
        onCancel={handleClose}
        title={success ? null : t("profile.removal.confirm")}
        centered
        width={440}
        closable={!loading}
        keyboard={!loading}
        mask={{ closable: !loading }}
        footer={
          success
            ? [
                <Button key="close" type="primary" onClick={handleClose}>
                  {t("app.close")}
                </Button>,
              ]
            : [
                <Button key="cancel" disabled={loading} onClick={handleClose}>
                  {t("app.cancel")}
                </Button>,
                <Button
                  key="delete"
                  danger
                  type="primary"
                  loading={loading}
                  onClick={handleDelete}
                >
                  {t("profile.removal.button")}
                </Button>,
              ]
        }
      >
        {success ? (
          <p>
            {t("profile.removal.successText")}{" "}
            <a
              href={`mailto:info@nextgis.${userStore.isRuRegion ? "ru" : "com"}`}
            >
              {`info@nextgis.${userStore.isRuRegion ? "ru" : "com"}`}
            </a>
          </p>
        ) : (
          <p>{t("profile.removal.description")}</p>
        )}
      </Modal>
    );
  },
);
